/* eslint-disable camelcase */
/**
 * @author Daniel Aguirre
 * @file saga.js
 * @description Clinic Phones Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Services
import clinicPhonesService from '@nephro/services/clinic-phones-service';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Constants
import {
  CREATE_CLINIC_PHONES,
  DELETE_CLINIC_PHONES_BY_ID,
  GET_ALL_CLINIC_PHONES,
  GET_CLINIC_PHONES_BY_ID,
  UPDATE_CLINIC_PHONES,
  GET_CLINIC_PHONES_BY_CLINIC_NAME
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get clinic phones from backend
 * @param { object } action - Action with payload data
 */
export function* getClinicPhones(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;

  try {
    const response = yield call(
      clinicPhonesService.getClinicPhones,
      paged,
      limit,
      page
    );

    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get clinic phones info by id
 * @param { object } action - Action with payload data
 */
export function* getClinicPhonesById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(clinicPhonesService.getClinicPhonesById, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.parameterNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to create clinic phones
 * @param { object } action - Action with payload data
 */
export function* createClinicPhones(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinic, phone } = data;

  try {
    const response = yield call(
      clinicPhonesService.createClinicPhones,
      clinic,
      phone
    );
    yield put(
      setResponse(
        'success',
        'new clinic phones',
        'paramMg.parameterCreated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to update clinic phones
 * @param { object } action - Action with payload data
 */
export function* updateClinicPhones(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinic, phone } = data;

  try {
    const response = yield call(
      clinicPhonesService.updateClinicPhones,
      clinic,
      phone
    );
    yield put(
      setResponse(
        'success',
        'updated clinic phones',
        'paramMg.parameterUpdated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to delete clinic phones info by id
 * @param { object } action - Action with payload data
 */
export function* deleteClinicPhonesById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(clinicPhonesService.deleteClinicPhonesById, id);
    yield put(
      setResponse(
        'success',
        'deleted clinic phones',
        'paramMg.parameterDeleted',
        { ...response },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

export function* getClinicPhonesByClinicName(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicName } = data;

  try {
    const response = yield call(
      clinicPhonesService.getClinicPhonesByClinicName,
      clinicName
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Declare all component sagas
 */
export function* watchClinicPhonesSaga() {
  yield takeLatest(GET_ALL_CLINIC_PHONES, getClinicPhones);
  yield takeLatest(GET_CLINIC_PHONES_BY_ID, getClinicPhonesById);
  yield takeLatest(CREATE_CLINIC_PHONES, createClinicPhones);
  yield takeLatest(UPDATE_CLINIC_PHONES, updateClinicPhones);
  yield takeLatest(DELETE_CLINIC_PHONES_BY_ID, deleteClinicPhonesById);
  yield takeLatest(DELETE_CLINIC_PHONES_BY_ID, deleteClinicPhonesById);
  yield takeLatest(
    GET_CLINIC_PHONES_BY_CLINIC_NAME,
    getClinicPhonesByClinicName
  );
}
